import { format } from "date-fns";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";

import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Pagination } from "@performyard/py-components";

import PageHeading from "../../../components/PageHeading";
import AllTodosTable from "./AllTodosTable";
import DownloadTodosCsvButton from "./DownloadTodosCsvButton";
import ReadableTodosFilterChips, { convertTodosFiltersToReadableList } from "./ReadableTodosFilterChips";
import TodosFilterPanel, { TodosFilters } from "./TodosFilterPanel";

import type { SortDirection } from "../../../api/hooks";
import { useRbacPrimaryRolePermissions, useWorkflowTodos } from "../../../api/hooks";
import { useIsNewNavigation } from "../../../api/hooks/defs/navigation";
import { useFeatureFlagQuery } from "../../../hooks/useFeatureFlag";

import type { WorkflowTodoFilterParams, WorkflowTodoSortBy } from "../../../api/services/defs/workflows";

import { getCompanySettingsContext, settingsHaveBeenFetched } from "../../../selectors/settings";

import InfoPageTemplate from "../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import { WORKFLOWS_NAVIGATION_LINKS } from "../navigationLinks";

const PAGE_TITLE = "Company To-Do's";
const PAGE_SIZE = 25;

// The sort params live in the URL, so they can hold arbitrary values (old bookmarks
// from the legacy page used other names). Fall back to the defaults instead of
// sending an invalid sort the API would reject.
const VALID_SORTS: WorkflowTodoSortBy[] = ["due_date", "action", "date_done"];
const sanitizeSortBy = (value: string): WorkflowTodoSortBy =>
	VALID_SORTS.includes(value as WorkflowTodoSortBy) ? (value as WorkflowTodoSortBy) : "due_date";
const sanitizeSortDirection = (value: string): SortDirection => (value === "desc" ? "desc" : "asc");

const OBJECT_ID_PATTERN = /^[0-9a-fA-F]{24}$/;

const isNamedReference = (value: unknown): value is { id: string; name: string } =>
	!!value &&
	typeof value === "object" &&
	typeof (value as { id: unknown }).id === "string" &&
	typeof (value as { name: unknown }).name === "string" &&
	OBJECT_ID_PATTERN.test((value as { id: string }).id);

// Runs before the query-state hook converts "date-..." strings back into Dates,
// so date keys are validated as serialized strings and passed through untouched.
const isSerializedDate = (value: unknown): value is string =>
	typeof value === "string" &&
	value.startsWith("date-") &&
	!Number.isNaN(new Date(value.replace("date-", "")).getTime());

const normalizeTodosFilters = (filters: Partial<TodosFilters>): Partial<TodosFilters> => {
	const normalized: Partial<TodosFilters> = {};
	for (const key of ["assignee", "workflow", "actor"] as const) {
		const value = filters[key];
		if (isNamedReference(value)) {
			normalized[key] = { id: value.id, name: value.name };
		}
	}
	if (filters.status === "active" || filters.status === "completed" || filters.status === "overdue") {
		normalized.status = filters.status;
	}
	for (const key of ["dueDateStart", "dueDateEnd", "completedDateStart", "completedDateEnd"] as const) {
		const value = filters[key];
		if (value instanceof Date || isSerializedDate(value)) {
			normalized[key] = value as Date;
		}
	}
	return normalized;
};

const toDateOnlyParam = (value?: Date): string | undefined => (value ? format(value, "yyyy-MM-dd") : undefined);

const AllTodos = () => {
	const isNewNav = useIsNewNavigation();
	const [isFiltersOpen, setIsFiltersOpen] = useState(false);

	const {
		value: workflowsFeatureVisible,
		query: { isLoading: isLoadingWorkflowsFF },
	} = useFeatureFlagQuery("ff-is-workflows-enabled", true);
	const { data: rbacPermissions, isLoading: isRbacLoading } = useRbacPrimaryRolePermissions("workflows");

	const companySettings = useSelector(getCompanySettingsContext);
	const haveSettingsBeenFetched = useSelector(settingsHaveBeenFetched);
	const workflowsEnabled = companySettings.getIn(["workflows", "state"], "disabled") === "enabled";

	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		filters,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState<TodosFilters>({
		defaultSortName: "due_date",
		defaultSortDirection: "asc",
		defaultPageNumber: 1,
		fallbackFilters: { status: "active" },
		normalizeFilters: normalizeTodosFilters,
	});

	const isGateLoading = isLoadingWorkflowsFF || isRbacLoading || !haveSettingsBeenFetched;
	const canBypassFeatureGate = rbacPermissions?.includes("workflows:bypassFeatureGate");
	const featureAvailable = canBypassFeatureGate || (workflowsFeatureVisible && workflowsEnabled);
	const hasViewPermission = rbacPermissions?.includes("workflows:viewAdmin");

	const filterParams: WorkflowTodoFilterParams = {
		action: debouncedSearchValue?.slice(0, 256) || undefined,
		subject: filters.assignee?.id,
		actor: filters.actor?.id,
		workflow: filters.workflow?.id,
		status: filters.status,
		dueDateStart: toDateOnlyParam(filters.dueDateStart),
		dueDateEnd: toDateOnlyParam(filters.dueDateEnd),
		completedDateStart: toDateOnlyParam(filters.completedDateStart),
		completedDateEnd: toDateOnlyParam(filters.completedDateEnd),
	};

	const { data, isLoading } = useWorkflowTodos(
		{
			...filterParams,
			sortBy: sanitizeSortBy(sort.name),
			sortOrder: sanitizeSortDirection(sort.direction),
			limit: PAGE_SIZE,
			skip: (pageNumber - 1) * PAGE_SIZE,
		},
		{ enabled: !isGateLoading && featureAvailable && !!hasViewPermission }
	);

	if (isGateLoading) {
		return null;
	}

	if (!featureAvailable) {
		return <Navigate to="/feature-disabled/" replace />;
	}

	if (!hasViewPermission) {
		return (
			<div className="p-6">
				<div className="bg-py-primary-1 flex w-[400px] gap-1.5 rounded-sm p-5">
					<FontAwesomeIcon icon={faInfoCircle} className="text-py-primary-7 text-lg" />
					<div className="font-semibold">Missing required permissions.</div>
				</div>
			</div>
		);
	}

	const todos = data?.todos ?? [];
	const totalCount = data?.totalCount ?? 0;
	const pageCount = Math.ceil(totalCount / PAGE_SIZE);

	const activeFilterChips = convertTodosFiltersToReadableList(filters);

	const actionButtons = [<DownloadTodosCsvButton key="download-todos-csv" params={filterParams} />];

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={setDebouncedSearch}
			onFilterButtonPress={() => setIsFiltersOpen(!isFiltersOpen)}
			activeFiltersCount={activeFilterChips.length}
			searchPlaceholder="Search To-Dos..."
		/>
	);

	const toolbarInformationRow = <ReadableTodosFilterChips filters={filters} setFilters={setFilters} />;

	const drawer = (
		<TodosFilterPanel
			isOpen={isFiltersOpen}
			onClose={() => setIsFiltersOpen(false)}
			filters={filters}
			onApplyFilters={(newFilters) => setFilters(newFilters)}
		/>
	);

	const mainContent = (
		<div>
			<AllTodosTable
				todos={todos}
				isLoading={isLoading}
				onSortChange={setSort}
				activeSort={sanitizeSortBy(sort.name)}
				activeSortDirection={sanitizeSortDirection(sort.direction)}
				emptyStateText="No To-Do’s Found"
			/>
			{pageCount > 1 && (
				<div data-testid="paginationContainer" className="mt-4 flex justify-center">
					<Pagination currentPage={pageNumber} pageCount={pageCount} onPageChange={setPageNumber} />
				</div>
			)}
		</div>
	);

	return (
		<InfoPageTemplate
			actionButtons={actionButtons}
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={toolbarInformationRow}
			drawer={drawer}
			mainContent={mainContent}
			pageTitle={PAGE_TITLE}
			pageHeading={isNewNav ? <PageHeading>{PAGE_TITLE}</PageHeading> : undefined}
			navigationLinks={WORKFLOWS_NAVIGATION_LINKS}
			activeNavigationLinkKey="all-todos"
		/>
	);
};

export default AllTodos;
